import { getSession } from '../session.js';
import { showToast } from './Toast.js';
import type { Transaction, ConstructionProject, Order } from '../../core/app-store.js';

type NavigateFn = (route: string, params?: Record<string, string>) => void;

export async function renderBalanceChecker(container: HTMLElement, navigate: NavigateFn): Promise<void> {
  const session = getSession();
  const industry = session?.industry ?? 'general';
  const businessId = session?.businessId ?? 'preview';

  if (industry === 'construction') {
    await renderConstructionBalance(container, navigate, businessId);
  } else if (industry === 'restaurant') {
    await renderRestaurantPOS(container, navigate, businessId);
  } else {
    await renderGeneralBalance(container, navigate, businessId);
  }
}

// ── CONSTRUCTION BALANCE ──────────────────────────────────────────────────────

async function renderConstructionBalance(container: HTMLElement, navigate: NavigateFn, businessId: string): Promise<void> {
  container.innerHTML = `
    <div class="page-header">
      <div style="display:flex;justify-content:space-between;align-items:flex-start">
        <div>
          <h1 class="page-title">💰 Project Financials</h1>
          <p class="page-subtitle">Track contract amounts, change orders, and payment balances per project.</p>
        </div>
        <button class="btn btn-gold" id="add-project-btn">+ New Project</button>
      </div>
    </div>

    <!-- New Project Modal -->
    <div id="project-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:1000;align-items:center;justify-content:center">
      <div class="card" style="width:500px;max-height:80vh;overflow-y:auto">
        <h3 style="font-size:15px;font-weight:700;margin-bottom:16px" id="proj-modal-title">New Project</h3>
        <div class="form-row-2">
          <div><label>Project Name *</label><input type="text" id="proj-name" placeholder="Downtown Office Renovation" /></div>
          <div><label>Client *</label><input type="text" id="proj-client" placeholder="Client Company LLC" /></div>
        </div>
        <div class="form-row-2">
          <div><label>Contract Amount ($) *</label><input type="number" id="proj-contract" min="0" step="0.01" placeholder="0.00" /></div>
          <div>
            <label>Status</label>
            <select id="proj-status">
              <option value="active">Active</option>
              <option value="on-hold">On Hold</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
        <div class="form-row-2">
          <div><label>Start Date</label><input type="date" id="proj-start" /></div>
          <div><label>End Date</label><input type="date" id="proj-end" /></div>
        </div>
        <div style="display:flex;gap:10px;justify-content:flex-end;margin-top:8px">
          <button class="btn btn-ghost" id="close-proj-modal">Cancel</button>
          <button class="btn btn-gold" id="save-proj-btn">Save Project</button>
        </div>
      </div>
    </div>

    <!-- Add Payment Modal -->
    <div id="payment-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:1000;align-items:center;justify-content:center">
      <div class="card" style="width:420px">
        <h3 style="font-size:15px;font-weight:700;margin-bottom:16px">Record Payment</h3>
        <input type="hidden" id="pay-project-id" />
        <div class="form-row">
          <label>Amount Received ($) *</label>
          <input type="number" id="pay-amount" min="0" step="0.01" placeholder="0.00" />
        </div>
        <div class="form-row">
          <label>Payment Method</label>
          <select id="pay-method">
            <option value="check">Check</option>
            <option value="wire">Wire Transfer</option>
            <option value="card">Card</option>
            <option value="cash">Cash</option>
          </select>
        </div>
        <div style="display:flex;gap:10px;justify-content:flex-end;margin-top:8px">
          <button class="btn btn-ghost" id="close-pay-modal">Cancel</button>
          <button class="btn btn-gold" id="save-pay-btn">Record Payment</button>
        </div>
      </div>
    </div>

    <div id="projects-content">
      <div class="empty-state"><div class="spinner"></div></div>
    </div>
  `;

  let editingProjectId: string | null = null;

  const openProjectModal = (proj?: ConstructionProject) => {
    editingProjectId = proj?.id ?? null;
    (document.getElementById('proj-modal-title') as HTMLElement).textContent = proj ? 'Edit Project' : 'New Project';
    (document.getElementById('proj-name') as HTMLInputElement).value = proj?.name ?? '';
    (document.getElementById('proj-client') as HTMLInputElement).value = proj?.client ?? '';
    (document.getElementById('proj-contract') as HTMLInputElement).value = proj ? String(proj.contractAmount) : '';
    (document.getElementById('proj-status') as HTMLSelectElement).value = proj?.status ?? 'active';
    (document.getElementById('proj-start') as HTMLInputElement).value = proj?.startDate?.slice(0, 10) ?? '';
    (document.getElementById('proj-end') as HTMLInputElement).value = proj?.endDate?.slice(0, 10) ?? '';
    document.getElementById('project-modal')!.style.display = 'flex';
  };

  document.getElementById('add-project-btn')!.addEventListener('click', () => openProjectModal());
  document.getElementById('close-proj-modal')!.addEventListener('click', () => {
    document.getElementById('project-modal')!.style.display = 'none';
  });
  document.getElementById('close-pay-modal')!.addEventListener('click', () => {
    document.getElementById('payment-modal')!.style.display = 'none';
  });

  document.getElementById('save-proj-btn')!.addEventListener('click', async () => {
    const name = (document.getElementById('proj-name') as HTMLInputElement).value.trim();
    const client = (document.getElementById('proj-client') as HTMLInputElement).value.trim();
    const contractAmount = parseFloat((document.getElementById('proj-contract') as HTMLInputElement).value);
    const status = (document.getElementById('proj-status') as HTMLSelectElement).value as ConstructionProject['status'];
    const startDate = (document.getElementById('proj-start') as HTMLInputElement).value || new Date().toISOString().slice(0, 10);
    const endDate = (document.getElementById('proj-end') as HTMLInputElement).value || undefined;

    if (!name || !client || isNaN(contractAmount)) { showToast('Name, client, and amount required', 'error'); return; }

    try {
      const api = getAPI();
      if (editingProjectId) {
        await api.updateProject(editingProjectId, { name, client, contractAmount, status, startDate, endDate });
        showToast('Project updated', 'success');
      } else {
        await api.createProject({ businessId, name, client, contractAmount, status, startDate, endDate });
        showToast('Project created', 'success');
      }
    } catch {
      showToast('Saved (preview mode)', 'info');
    }
    document.getElementById('project-modal')!.style.display = 'none';
    editingProjectId = null;
    await loadConstruction(businessId, openProjectModal);
  });

  document.getElementById('save-pay-btn')!.addEventListener('click', async () => {
    const projectId = (document.getElementById('pay-project-id') as HTMLInputElement).value;
    const amount = parseFloat((document.getElementById('pay-amount') as HTMLInputElement).value);
    const paymentMethod = (document.getElementById('pay-method') as HTMLSelectElement).value;
    if (!amount || isNaN(amount)) { showToast('Amount required', 'error'); return; }

    try {
      await getAPI().createTransaction({ businessId, amount, paymentMethod: paymentMethod as 'cash' | 'card' | 'tab', items: [], projectId });
      showToast(`Payment of $${amount.toFixed(2)} recorded`, 'success');
    } catch {
      showToast('Payment recorded (preview)', 'info');
    }
    document.getElementById('payment-modal')!.style.display = 'none';
    await loadConstruction(businessId, openProjectModal);
  });

  await loadConstruction(businessId, openProjectModal);
}

async function loadConstruction(businessId: string, openProjectModal: (p?: ConstructionProject) => void): Promise<void> {
  let projects: ConstructionProject[] = [];
  let transactions: Transaction[] = [];
  try {
    const api = getAPI();
    [projects, transactions] = await Promise.all([
      api.getProjects(businessId) as Promise<ConstructionProject[]>,
      api.getTransactions(businessId) as Promise<Transaction[]>,
    ]);
  } catch {
    projects = getSampleProjects(businessId);
    transactions = getSampleTransactions(businessId);
  }

  const content = document.getElementById('projects-content')!;

  if (projects.length === 0) {
    content.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">🏗</div>
        <div class="empty-state-title">No Projects Yet</div>
        <div class="empty-state-desc">Add your first construction project to track financials.</div>
      </div>
    `;
    return;
  }

  const statusColor: Record<string, string> = { active: '#10b981', 'on-hold': '#f59e0b', completed: '#3b82f6' };

  content.innerHTML = projects.map((proj) => {
    const paid = transactions
      .filter((t) => t.projectId === proj.id)
      .reduce((s, t) => s + t.amount, 0);
    const balance = proj.contractAmount - paid;
    const pct = proj.contractAmount > 0 ? Math.min((paid / proj.contractAmount) * 100, 100) : 0;

    return `
      <div class="card" style="margin-bottom:16px">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px;flex-wrap:wrap;gap:8px">
          <div>
            <div style="font-size:16px;font-weight:700">${escHtml(proj.name)}</div>
            <div style="font-size:12px;color:var(--text-muted)">${escHtml(proj.client)} · ${proj.startDate?.slice(0, 10) ?? ''}</div>
          </div>
          <div style="display:flex;gap:8px;align-items:center">
            <span style="font-size:11px;font-weight:700;color:${statusColor[proj.status] ?? '#64748b'};background:${statusColor[proj.status] ?? '#64748b'}22;padding:3px 10px;border-radius:20px">${proj.status.toUpperCase()}</span>
            <button class="btn btn-ghost btn-sm" data-action="edit" data-id="${proj.id}">Edit</button>
            <button class="btn btn-gold btn-sm" data-action="payment" data-id="${proj.id}">+ Payment</button>
          </div>
        </div>

        <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:14px">
          <div class="stat-card" style="padding:12px">
            <div style="font-size:13px;font-weight:700;color:var(--text-primary)">$${proj.contractAmount.toLocaleString()}</div>
            <div style="font-size:10px;color:var(--text-muted)">Contract Value</div>
          </div>
          <div class="stat-card" style="padding:12px">
            <div style="font-size:13px;font-weight:700;color:#10b981">$${paid.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
            <div style="font-size:10px;color:var(--text-muted)">Received</div>
          </div>
          <div class="stat-card" style="padding:12px">
            <div style="font-size:13px;font-weight:700;color:${balance > 0 ? '#f59e0b' : '#10b981'}">$${balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
            <div style="font-size:10px;color:var(--text-muted)">Balance Due</div>
          </div>
          <div class="stat-card" style="padding:12px">
            <div style="font-size:13px;font-weight:700;color:var(--text-primary)">${pct.toFixed(0)}%</div>
            <div style="font-size:10px;color:var(--text-muted)">Paid</div>
          </div>
        </div>

        <div style="background:var(--bg-tertiary);border-radius:4px;height:8px;overflow:hidden">
          <div style="height:100%;background:${pct >= 100 ? '#10b981' : '#2563eb'};width:${pct}%;transition:width .3s"></div>
        </div>

        ${transactions.filter((t) => t.projectId === proj.id).length > 0 ? `
          <div style="margin-top:12px;font-size:11px;color:var(--text-muted)">
            <strong style="color:var(--text-secondary)">Payment History:</strong>
            ${transactions.filter((t) => t.projectId === proj.id).map((t) =>
              `<span style="margin-left:8px">$${t.amount.toLocaleString()} on ${t.createdAt.slice(0, 10)} (${t.paymentMethod})</span>`
            ).join(' ·')}
          </div>
        ` : ''}
      </div>
    `;
  }).join('');

  content.querySelectorAll('[data-action]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const action = btn.getAttribute('data-action')!;
      const id = btn.getAttribute('data-id')!;
      if (action === 'edit') {
        const proj = projects.find((p) => p.id === id);
        if (proj) openProjectModal(proj);
      } else if (action === 'payment') {
        (document.getElementById('pay-project-id') as HTMLInputElement).value = id;
        (document.getElementById('pay-amount') as HTMLInputElement).value = '';
        document.getElementById('payment-modal')!.style.display = 'flex';
      }
    });
  });
}

// ── RESTAURANT POS / SALES DASHBOARD ──────────────────────────────────────────

async function renderRestaurantPOS(container: HTMLElement, navigate: NavigateFn, businessId: string): Promise<void> {
  container.innerHTML = `
    <div class="page-header">
      <div style="display:flex;justify-content:space-between;align-items:flex-start">
        <div>
          <h1 class="page-title">💳 Point of Sale & Revenue</h1>
          <p class="page-subtitle">Daily sales, payment processing, and revenue analytics.</p>
        </div>
        <div style="display:flex;gap:10px">
          <button class="btn btn-ghost" id="customers-btn">👥 Customers</button>
          <button class="btn btn-gold" id="checkout-btn">+ New Sale</button>
        </div>
      </div>
    </div>

    <!-- Period selector -->
    <div style="display:flex;gap:8px;margin-bottom:20px">
      ${['today', '7d', '30d', 'all'].map((p, i) => `
        <button class="filter-chip ${i === 0 ? 'active' : ''}" data-period="${p}">
          ${p === 'today' ? 'Today' : p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : 'All Time'}
        </button>
      `).join('')}
    </div>

    <!-- Stat cards -->
    <div class="stats-grid" style="margin-bottom:24px">
      <div class="stat-card"><div class="stat-value" id="pos-revenue">—</div><div class="stat-label">Revenue</div></div>
      <div class="stat-card"><div class="stat-value" id="pos-orders">—</div><div class="stat-label">Orders</div></div>
      <div class="stat-card"><div class="stat-value" id="pos-avg">—</div><div class="stat-label">Avg Check</div></div>
      <div class="stat-card"><div class="stat-value" id="pos-tips">—</div><div class="stat-label">Tips Collected</div></div>
    </div>

    <!-- Checkout Modal -->
    <div id="checkout-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:1000;align-items:center;justify-content:center">
      <div class="card" style="width:480px;max-height:80vh;overflow-y:auto">
        <h3 style="font-size:15px;font-weight:700;margin-bottom:16px">Process Payment</h3>
        <div class="form-row-2">
          <div>
            <label>Sale Amount ($) *</label>
            <input type="number" id="checkout-amount" min="0" step="0.01" placeholder="0.00" />
          </div>
          <div>
            <label>Tip ($)</label>
            <input type="number" id="checkout-tip" min="0" step="0.01" placeholder="0.00" />
          </div>
        </div>
        <div class="form-row-2">
          <div>
            <label>Server / Employee *</label>
            <select id="checkout-server">
              <option value="">— Unassigned —</option>
            </select>
          </div>
          <div>
            <label>Table / Order Ref</label>
            <input type="text" id="checkout-ref" placeholder="Table 5, Order #..." />
          </div>
        </div>
        <div class="form-row">
          <label>Payment Method</label>
          <select id="checkout-method">
            <option value="card">Card</option>
            <option value="cash">Cash</option>
            <option value="tab">Tab / House Account</option>
          </select>
        </div>
        <div class="form-row-2">
          <div>
            <label>Customer Name</label>
            <input type="text" id="checkout-customer" placeholder="Optional" />
          </div>
          <div>
            <label>Customer Phone (for SMS receipt)</label>
            <input type="tel" id="checkout-phone" placeholder="+1 360 555 0100" />
          </div>
        </div>
        <div class="form-row">
          <label>Customer Email (for email receipt)</label>
          <input type="email" id="checkout-email" placeholder="customer@example.com" />
        </div>
        <!-- Quick tip buttons -->
        <div style="display:flex;gap:8px;margin-bottom:12px;flex-wrap:wrap">
          <span style="font-size:11px;color:var(--text-muted);align-self:center">Quick tip:</span>
          ${[15, 18, 20, 25].map((pct) => `
            <button class="btn btn-ghost btn-sm tip-pct-btn" data-pct="${pct}">${pct}%</button>
          `).join('')}
        </div>
        <div style="display:flex;gap:10px;justify-content:flex-end">
          <button class="btn btn-ghost" id="close-checkout-modal">Cancel</button>
          <button class="btn btn-gold btn-lg" id="process-payment-btn">Process Payment</button>
        </div>
      </div>
    </div>

    <!-- Two column layout -->
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px">
      <!-- Recent transactions -->
      <div class="card" style="padding:0;overflow:hidden">
        <div style="padding:14px 16px;border-bottom:1px solid var(--border);font-size:13px;font-weight:700">Recent Transactions</div>
        <div id="pos-transactions" style="overflow-y:auto;max-height:400px">
          <div style="text-align:center;padding:40px"><div class="spinner" style="margin:0 auto"></div></div>
        </div>
      </div>
      <!-- Revenue by category / hour -->
      <div class="card">
        <div style="font-size:13px;font-weight:700;margin-bottom:16px">Sales Breakdown</div>
        <div id="pos-breakdown">
          <div style="text-align:center;padding:40px"><div class="spinner" style="margin:0 auto"></div></div>
        </div>
      </div>
    </div>
  `;

  document.getElementById('customers-btn')!.addEventListener('click', () => navigate('customers'));
  document.getElementById('close-checkout-modal')!.addEventListener('click', () => {
    document.getElementById('checkout-modal')!.style.display = 'none';
  });
  document.getElementById('checkout-btn')!.addEventListener('click', () => {
    (document.getElementById('checkout-amount') as HTMLInputElement).value = '';
    (document.getElementById('checkout-tip') as HTMLInputElement).value = '';
    (document.getElementById('checkout-customer') as HTMLInputElement).value = '';
    (document.getElementById('checkout-phone') as HTMLInputElement).value = '';
    (document.getElementById('checkout-email') as HTMLInputElement).value = '';
    (document.getElementById('checkout-ref') as HTMLInputElement).value = '';
    (document.getElementById('checkout-server') as HTMLSelectElement).value = '';
    document.getElementById('checkout-modal')!.style.display = 'flex';
  });

  // Quick tip buttons
  document.querySelectorAll('.tip-pct-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const pct = parseInt(btn.getAttribute('data-pct')!) / 100;
      const amount = parseFloat((document.getElementById('checkout-amount') as HTMLInputElement).value) || 0;
      (document.getElementById('checkout-tip') as HTMLInputElement).value = (amount * pct).toFixed(2);
    });
  });

  document.getElementById('process-payment-btn')!.addEventListener('click', async () => {
    const amount = parseFloat((document.getElementById('checkout-amount') as HTMLInputElement).value);
    const tip = parseFloat((document.getElementById('checkout-tip') as HTMLInputElement).value) || 0;
    const paymentMethod = (document.getElementById('checkout-method') as HTMLSelectElement).value as 'cash' | 'card' | 'tab';
    const customerName = (document.getElementById('checkout-customer') as HTMLInputElement).value.trim();
    const customerPhone = (document.getElementById('checkout-phone') as HTMLInputElement).value.trim();
    const customerEmail = (document.getElementById('checkout-email') as HTMLInputElement).value.trim();
    const ref = (document.getElementById('checkout-ref') as HTMLInputElement).value.trim();
    const serverSelect = document.getElementById('checkout-server') as HTMLSelectElement;
    const serverId = serverSelect.value || undefined;
    const serverName = serverId ? serverSelect.selectedOptions[0]?.text : undefined;

    if (!amount || isNaN(amount)) { showToast('Amount required', 'error'); return; }

    try {
      const api = getAPI();
      let customerId: string | undefined;
      if (customerName) {
        const customer = await api.upsertCustomer(businessId, customerName, customerPhone || undefined, customerEmail || undefined) as { id: string };
        customerId = customer.id;
        await api.recordCustomerPurchase(customerId, amount + tip, []);
      }
      await api.createTransaction({
        businessId,
        amount,
        tip,
        paymentMethod,
        items: [],
        customerId,
        customerName: customerName || undefined,
        serverId,
        serverName,
      });
      showToast(`$${(amount + tip).toFixed(2)} processed`, 'success');

      // Send receipt if contact info provided
      if (customerPhone || customerEmail) {
        const receiptBody = buildReceiptText(amount, tip, paymentMethod, ref, customerName);
        if (customerPhone) {
          try {
            await api.sendSMS({ to: customerPhone, body: receiptBody });
            showToast(`SMS receipt sent to ${customerPhone}`, 'info');
          } catch {
            showToast('SMS receipt queued (configure Twilio in Settings)', 'info');
          }
        }
        if (customerEmail) {
          try {
            await (api.sendInvitationEmail as (...a: unknown[]) => Promise<unknown>)({
              toEmail: customerEmail,
              toName: customerName || undefined,
              businessName: 'Your Restaurant',
              industry: 'restaurant',
              personalMessage: receiptBody,
              fromName: 'Preuve AI POS',
            });
            showToast(`Email receipt sent to ${customerEmail}`, 'info');
          } catch {
            showToast('Email receipt queued (configure SendGrid in Settings)', 'info');
          }
        }
      }
    } catch {
      showToast(`$${(amount + tip).toFixed(2)} processed (preview mode)`, 'success');
    }
    document.getElementById('checkout-modal')!.style.display = 'none';
    await loadPOS(businessId, 'today');
  });

  // Period filter
  document.querySelectorAll('[data-period]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      document.querySelectorAll('[data-period]').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      await loadPOS(businessId, btn.getAttribute('data-period') as string);
    });
  });

  // Populate server dropdown
  try {
    const employees = await getAPI().getEmployees(businessId) as Array<{ id: string; name: string; role: string; active: boolean }>;
    const serverSel = document.getElementById('checkout-server') as HTMLSelectElement;
    employees.filter((e) => e.active && e.role !== 'kitchen').forEach((e) => {
      const opt = document.createElement('option');
      opt.value = e.id;
      opt.textContent = `${e.name} (${e.role})`;
      serverSel.appendChild(opt);
    });
  } catch {
    // preview — no employees loaded
  }

  await loadPOS(businessId, 'today');
}

async function loadPOS(businessId: string, period: string): Promise<void> {
  const since = periodToSince(period);
  let transactions: Transaction[] = [];
  try {
    transactions = await getAPI().getTransactions(businessId, since) as Transaction[];
  } catch {
    transactions = getSampleTransactions(businessId, since);
  }

  const revenue = transactions.reduce((s, t) => s + t.amount, 0);
  const tips = transactions.reduce((s, t) => s + (t.tip ?? 0), 0);
  const avg = transactions.length ? revenue / transactions.length : 0;

  (document.getElementById('pos-revenue') as HTMLElement).textContent = `$${revenue.toFixed(2)}`;
  (document.getElementById('pos-orders') as HTMLElement).textContent = String(transactions.length);
  (document.getElementById('pos-avg') as HTMLElement).textContent = `$${avg.toFixed(2)}`;
  (document.getElementById('pos-tips') as HTMLElement).textContent = `$${tips.toFixed(2)}`;

  // Recent transactions list
  const txEl = document.getElementById('pos-transactions')!;
  if (transactions.length === 0) {
    txEl.innerHTML = `<div style="text-align:center;padding:40px;color:var(--text-muted)">No transactions in this period.</div>`;
  } else {
    txEl.innerHTML = transactions.slice(0, 50).map((t) => `
      <div style="display:flex;justify-content:space-between;align-items:center;padding:10px 16px;border-bottom:1px solid var(--border);font-size:13px">
        <div>
          <div style="font-weight:600">${t.customerName ? escHtml(t.customerName) : 'Walk-in'}</div>
          <div style="font-size:11px;color:var(--text-muted)">${new Date(t.createdAt).toLocaleString()} · ${t.paymentMethod}</div>
        </div>
        <div style="text-align:right">
          <div style="font-weight:700;color:var(--brand-gold)">$${t.amount.toFixed(2)}</div>
          ${t.tip ? `<div style="font-size:11px;color:var(--text-muted)">+$${t.tip.toFixed(2)} tip</div>` : ''}
        </div>
      </div>
    `).join('');
  }

  // Breakdown by payment method
  const methods: Record<string, number> = {};
  for (const t of transactions) {
    methods[t.paymentMethod] = (methods[t.paymentMethod] ?? 0) + t.amount;
  }
  const breakdownEl = document.getElementById('pos-breakdown')!;
  const maxVal = Math.max(...Object.values(methods), 1);
  breakdownEl.innerHTML = Object.entries(methods).length === 0
    ? `<div style="color:var(--text-muted);font-size:13px">No data for this period.</div>`
    : Object.entries(methods).map(([method, amount]) => `
        <div style="margin-bottom:12px">
          <div style="display:flex;justify-content:space-between;font-size:12px;margin-bottom:4px">
            <span style="text-transform:capitalize;color:var(--text-secondary)">${method}</span>
            <span style="font-weight:700">$${amount.toFixed(2)}</span>
          </div>
          <div style="background:var(--bg-tertiary);border-radius:4px;height:6px">
            <div style="height:100%;background:var(--brand-gold);width:${(amount / maxVal) * 100}%;border-radius:4px"></div>
          </div>
        </div>
      `).join('') + `
      <div style="border-top:1px solid var(--border);padding-top:12px;margin-top:4px">
        <div style="font-size:12px;color:var(--text-muted);margin-bottom:8px">Tips by method</div>
        ${Object.entries(
            transactions.reduce((acc, t) => {
              if (t.tip) acc[t.paymentMethod] = (acc[t.paymentMethod] ?? 0) + t.tip;
              return acc;
            }, {} as Record<string, number>)
          ).map(([method, amount]) => `
            <div style="display:flex;justify-content:space-between;font-size:12px;margin-bottom:4px">
              <span style="text-transform:capitalize;color:var(--text-secondary)">${method}</span>
              <span>$${amount.toFixed(2)}</span>
            </div>
          `).join('') || `<div style="font-size:12px;color:var(--text-muted)">No tips recorded.</div>`
        }
      </div>
    `;
}

// ── GENERAL BALANCE ────────────────────────────────────────────────────────────

async function renderGeneralBalance(container: HTMLElement, navigate: NavigateFn, businessId: string): Promise<void> {
  container.innerHTML = `
    <div class="page-header">
      <h1 class="page-title">💰 Financial Overview</h1>
      <p class="page-subtitle">Track your revenue and payment history.</p>
    </div>
    <div class="empty-state">
      <div class="empty-state-icon">💼</div>
      <div class="empty-state-title">General Business Finance</div>
      <div class="empty-state-desc">Use the POS feature to record transactions and track payments.</div>
      <button class="btn btn-gold" id="pos-btn" style="margin-top:16px">Open POS</button>
    </div>
  `;
  document.getElementById('pos-btn')?.addEventListener('click', () => navigate('balance'));
}

// ── HELPERS ───────────────────────────────────────────────────────────────────

function periodToSince(period: string): string | undefined {
  const now = new Date();
  if (period === 'today') {
    now.setHours(0, 0, 0, 0);
    return now.toISOString();
  }
  if (period === '7d') return new Date(Date.now() - 7 * 86400000).toISOString();
  if (period === '30d') return new Date(Date.now() - 30 * 86400000).toISOString();
  return undefined;
}

function getSampleProjects(businessId: string): ConstructionProject[] {
  return [
    { id: 'pr1', businessId, name: 'Downtown Office Renovation', client: 'Apex Holdings LLC', contractAmount: 485000, startDate: '2025-01-15', status: 'active' },
    { id: 'pr2', businessId, name: 'Residential Complex — Phase 2', client: 'Greenway Developers', contractAmount: 1200000, startDate: '2024-08-01', endDate: '2025-06-30', status: 'active' },
    { id: 'pr3', businessId, name: 'Medical Clinic Build-Out', client: 'Summit Health Group', contractAmount: 320000, startDate: '2024-03-01', endDate: '2024-12-15', status: 'completed' },
  ];
}

function getSampleTransactions(businessId: string, since?: string): Transaction[] {
  const now = new Date();
  const ago = (days: number) => new Date(now.getTime() - days * 86400000).toISOString();
  const all: Transaction[] = [
    { id: 'tx1', businessId, projectId: 'pr1', amount: 145500, paymentMethod: 'card', items: [], createdAt: ago(30) },
    { id: 'tx2', businessId, projectId: 'pr1', amount: 97000, paymentMethod: 'card', items: [], createdAt: ago(15) },
    { id: 'tx3', businessId, projectId: 'pr2', amount: 360000, paymentMethod: 'card', items: [], createdAt: ago(90) },
    { id: 'tx4', businessId, projectId: 'pr3', amount: 320000, paymentMethod: 'card', items: [], createdAt: ago(60) },
    { id: 'tx5', businessId, amount: 89.50, tip: 15, paymentMethod: 'card', items: [], customerName: 'Sarah M.', createdAt: ago(0) },
    { id: 'tx6', businessId, amount: 42.00, tip: 8, paymentMethod: 'cash', items: [], createdAt: ago(0) },
    { id: 'tx7', businessId, amount: 67.25, paymentMethod: 'card', items: [], customerName: 'James R.', createdAt: ago(1) },
  ];
  if (since) return all.filter((t) => t.createdAt >= since);
  return all;
}

function buildReceiptText(amount: number, tip: number, method: string, ref: string, name: string): string {
  const total = (amount + tip).toFixed(2);
  const lines = [
    '🧾 Receipt from Preuve AI',
    ref ? `Order: ${ref}` : '',
    name ? `Customer: ${name}` : '',
    `Amount: $${amount.toFixed(2)}`,
    tip > 0 ? `Tip: $${tip.toFixed(2)}` : '',
    `Total: $${total}`,
    `Payment: ${method}`,
    `Date: ${new Date().toLocaleDateString()}`,
    'Thank you!',
  ].filter(Boolean);
  return lines.join('\n');
}

function getAPI() {
  return (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
}
function escHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
